// src/modules/operaciones/inventarios/dashboard/PanelBodegas.js
//
// Pestaña "Bodegas" del dashboard de mermas:
//   1. Barras agrupadas: merma conocida vs desconocida por bodega
//   2. Ranking de bodegas por merma neta (peor arriba, rojo/verde por signo)
//   3. Mapa de calor bodega × tipo (grilla CSS, colores con colorDivergente)
//   4. Entrada vs salida (solo bodegas que traen ese detalle: San Jorge y CEDI)
//
// USO
//   <PanelBodegas analisis={d.analisis} />
//
// Espera analisis.bodegas = { lista, ranking, mapaCalor, movimientos } (salida de analizarMermas).
//
// IMPORTANTE (se aclara en pantalla): "entrada" y "salida" NO se suman a la merma conocida,
// son una parte de ella (San Jorge y CEDI separan su merma conocida en esos dos movimientos).

import React, { useMemo } from "react";
import { Bar, BarChart, CartesianGrid, Cell, Legend, ReferenceLine, Tooltip, XAxis, YAxis } from "recharts";
import { BarChart3 } from "lucide-react";

import ChartCard from "./ChartCard";
import ChartTooltip from "./ChartTooltip";
import {
  COLORES_SEMANTICOS,
  colorDivergente,
  colorPorSigno,
  fmtCompacto,
  fmtMoneda,
} from "../utils/informesComun";

/* ========================================================================== */
/* Ejes comunes                                                               */
/* ========================================================================== */

const TICK = { fontSize: 11, fill: COLORES_SEMANTICOS.texto };
const margenVertical = { top: 16, right: 16, left: 8, bottom: 8 };

// Da aire al dominio para que no se corten las etiquetas de las barras extremas
const dominioConAire = [(min) => Math.min(min * 1.25, 0), (max) => Math.max(max * 1.25, 0)];

/* ========================================================================== */
/* 1. Barras agrupadas: conocida vs desconocida                               */
/* ========================================================================== */

function GraficoConocidaDesconocida({ datos }) {
  return (
    <BarChart data={datos} margin={{ ...margenVertical, bottom: 4 }}>
      <CartesianGrid vertical={false} stroke={COLORES_SEMANTICOS.grilla} />
      <XAxis
        dataKey="etiqueta"
        tick={TICK}
        interval={0}
        angle={-25}
        textAnchor="end"
        height={64}
        tickLine={false}
      />
      <YAxis tickFormatter={(v) => fmtCompacto(v)} tick={TICK} width={72} tickLine={false} axisLine={false} />
      <ReferenceLine y={0} stroke="#9ca3af" />
      <Tooltip
        cursor={{ fill: "rgba(0,0,0,0.04)" }}
        content={
          <ChartTooltip
            formato="moneda"
            colorearSigno
            filasExtra={(f) => [{ etiqueta: "Merma neta", valor: f.neta, formato: "moneda" }]}
          />
        }
      />
      <Legend verticalAlign="top" height={28} iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
      <Bar dataKey="conocida" name="Merma conocida" fill={COLORES_SEMANTICOS.conocida} maxBarSize={36} />
      <Bar dataKey="desconocida" name="Merma desconocida" fill={COLORES_SEMANTICOS.desconocida} maxBarSize={36} />
    </BarChart>
  );
}

/* ========================================================================== */
/* 2. Ranking por merma neta (horizontal)                                     */
/* ========================================================================== */

function GraficoRanking({ datos }) {
  // Etiqueta de valor al extremo de cada barra (funciona con barras negativas y positivas)
  const etiquetaBarra = (props) => {
    const { x, y, width, height, index } = props;
    const d = datos[index];
    if (!d || x === undefined) return null;
    const izquierda = Math.min(x, x + width);
    const derecha = Math.max(x, x + width);
    const negativo = d.neta < 0;
    return (
      <text
        x={negativo ? izquierda - 6 : derecha + 6}
        y={y + height / 2}
        textAnchor={negativo ? "end" : "start"}
        dominantBaseline="middle"
        fontSize={11}
        fontWeight={600}
        fill="#374151"
      >
        {fmtCompacto(d.neta)}
      </text>
    );
  };

  return (
    <BarChart data={datos} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
      <CartesianGrid horizontal={false} stroke={COLORES_SEMANTICOS.grilla} />
      <XAxis
        type="number"
        domain={dominioConAire}
        tickFormatter={(v) => fmtCompacto(v)}
        tick={TICK}
        tickLine={false}
        axisLine={false}
      />
      <YAxis type="category" dataKey="etiqueta" tick={{ ...TICK, fill: "#374151" }} width={110} tickLine={false} />
      <ReferenceLine x={0} stroke="#9ca3af" />
      <Tooltip
        cursor={{ fill: "rgba(0,0,0,0.04)" }}
        content={
          <ChartTooltip
            formato="moneda"
            colorearSigno
            filasExtra={(f) => [
              { etiqueta: "Desconocida", valor: f.desconocida, color: COLORES_SEMANTICOS.desconocida },
              { etiqueta: "Conocida", valor: f.conocida, color: COLORES_SEMANTICOS.conocida },
            ]}
          />
        }
      />
      <Bar dataKey="neta" name="Merma neta" maxBarSize={26} label={etiquetaBarra}>
        {datos.map((d) => (
          <Cell key={d.clave} fill={colorPorSigno(d.neta)} />
        ))}
      </Bar>
    </BarChart>
  );
}

/* ========================================================================== */
/* 3. Mapa de calor (grilla CSS)                                              */
/* ========================================================================== */

// Para que los valores pequeños también se vean, la intensidad usa raíz cuadrada
// (un valor 100 veces menor que el máximo se pinta al 10 %, no al 1 %).
const escalaSuave = (v) => Math.sign(v) * Math.sqrt(Math.abs(v));

const NOTA_COLUMNA = { entrada: "de la conocida", salida: "de la conocida" };

function MapaCalor({ mapa }) {
  const { columnas, filas, maxAbs } = mapa;
  const maxEscala = escalaSuave(maxAbs);
  const plantilla = `minmax(110px, 1.3fr) repeat(${columnas.length}, minmax(84px, 1fr))`;

  return (
    <div className="overflow-x-auto">
      <div role="table" aria-label="Mapa de calor de mermas por bodega" style={{ minWidth: 460 }}>
        {/* Encabezado */}
        <div role="row" className="grid gap-1 mb-1" style={{ gridTemplateColumns: plantilla }}>
          <div role="columnheader" />
          {columnas.map((c) => (
            <div key={c.id} role="columnheader" className="text-center text-xs font-medium text-gray-600 leading-tight">
              {c.etiqueta}
              {NOTA_COLUMNA[c.id] && <span className="block text-[10px] font-normal text-gray-400">{NOTA_COLUMNA[c.id]}</span>}
            </div>
          ))}
        </div>

        {/* Filas */}
        {filas.map((f) => (
          <div key={f.clave} role="row" className="grid gap-1 mb-1" style={{ gridTemplateColumns: plantilla }}>
            <div role="rowheader" className="flex items-center text-sm font-medium text-gray-800 truncate pr-2" title={f.etiqueta}>
              {f.etiqueta}
            </div>
            {columnas.map((c) => {
              const v = f.valores?.[c.id];
              if (v === null || v === undefined) {
                return (
                  <div
                    key={c.id}
                    role="cell"
                    className="flex items-center justify-center rounded-md bg-gray-50 text-xs text-gray-300 h-10"
                    title="No aplica: esta bodega no separa este detalle"
                  >
                    n/a
                  </div>
                );
              }
              const intensidad = maxEscala > 0 ? Math.min(Math.abs(escalaSuave(v)) / maxEscala, 1) : 0;
              return (
                <div
                  key={c.id}
                  role="cell"
                  className="flex items-center justify-center rounded-md text-xs font-semibold tabular-nums h-10"
                  style={{
                    backgroundColor: colorDivergente(escalaSuave(v), maxEscala),
                    color: intensidad > 0.62 ? "#ffffff" : "#1f2937",
                  }}
                  title={`${f.etiqueta} · ${c.etiqueta}: ${fmtMoneda(v)}`}
                >
                  {fmtCompacto(v)}
                </div>
              );
            })}
          </div>
        ))}

        {/* Leyenda */}
        <div className="flex items-center gap-2 mt-3 text-[11px] text-gray-500">
          <span>Pérdida</span>
          <span
            className="h-2 flex-1 max-w-[180px] rounded-full border border-gray-100"
            style={{ background: `linear-gradient(to right, ${COLORES_SEMANTICOS.perdida}, #f8fafc, ${COLORES_SEMANTICOS.sobrante})` }}
            aria-hidden="true"
          />
          <span>Sobrante</span>
          <span className="text-gray-400">· intensidad en escala suave</span>
        </div>
      </div>
    </div>
  );
}

/* ========================================================================== */
/* 4. Entrada vs salida                                                       */
/* ========================================================================== */

function GraficoEntradaSalida({ datos }) {
  return (
    <BarChart data={datos} margin={margenVertical}>
      <CartesianGrid vertical={false} stroke={COLORES_SEMANTICOS.grilla} />
      <XAxis dataKey="etiqueta" tick={TICK} interval={0} tickLine={false} />
      <YAxis tickFormatter={(v) => fmtCompacto(v)} tick={TICK} width={72} tickLine={false} axisLine={false} />
      <ReferenceLine y={0} stroke="#9ca3af" />
      <Tooltip
        cursor={{ fill: "rgba(0,0,0,0.04)" }}
        content={
          <ChartTooltip
            formato="moneda"
            colorearSigno
            filasExtra={(f) => [{ etiqueta: "Neto (entrada + salida)", valor: f.neto, formato: "moneda" }]}
          />
        }
      />
      <Legend verticalAlign="top" height={28} iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
      <Bar dataKey="entrada" name="Entrada" fill={COLORES_SEMANTICOS.entrada} maxBarSize={56} />
      <Bar dataKey="salida" name="Salida" fill={COLORES_SEMANTICOS.salida} maxBarSize={56} />
    </BarChart>
  );
}

/* ========================================================================== */
/* Panel                                                                      */
/* ========================================================================== */

function SinDatos() {
  return (
    <div className="flex flex-col items-center justify-center text-center bg-white border border-dashed border-gray-300 rounded-xl py-16 px-4">
      <BarChart3 className="w-10 h-10 text-gray-300 mb-3" aria-hidden="true" />
      <p className="text-sm font-medium text-gray-700">No hay datos de bodegas</p>
      <p className="text-xs text-gray-500 mt-1">Ajusta los filtros o revisa que el archivo tenga mermas por bodega.</p>
    </div>
  );
}

export function PanelBodegas({ analisis }) {
  const b = analisis?.bodegas;
  const lista = useMemo(() => b?.lista || [], [b]);
  const ranking = useMemo(() => b?.ranking || [], [b]);
  const movimientos = useMemo(() => b?.movimientos || [], [b]);
  const mapa = b?.mapaCalor;

  if (!b || lista.length === 0) return <SinDatos />;

  const alturaRanking = Math.max(340, ranking.length * 40 + 60);
  const hayMapa = mapa && mapa.filas?.length > 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
      <ChartCard
        className="lg:col-span-3"
        titulo="Merma conocida vs desconocida por bodega"
        subtitulo="Valores en $ · negativo = faltante, positivo = sobrante"
        height={340}
      >
        <GraficoConocidaDesconocida datos={lista} />
      </ChartCard>

      <ChartCard
        className="lg:col-span-2"
        titulo="Ranking de bodegas por merma neta"
        subtitulo="De peor a mejor (conocida + desconocida)"
        height={alturaRanking}
      >
        <GraficoRanking datos={ranking} />
      </ChartCard>

      <ChartCard
        className="lg:col-span-3"
        titulo="Mapa de calor: bodega × tipo de merma"
        subtitulo="Entrada y salida son una parte de la merma conocida (solo San Jorge y CEDI las separan)"
        responsive={false}
        height="auto"
        pantallaCompleta={false}
        vacio={!hayMapa}
      >
        {hayMapa && <MapaCalor mapa={mapa} />}
      </ChartCard>

      <ChartCard
        className="lg:col-span-2"
        titulo="Entrada vs salida"
        subtitulo="Solo bodegas que registran ambos movimientos"
        height={300}
        vacio={movimientos.length === 0}
        mensajeVacio="Ninguna de las bodegas visibles separa entradas y salidas."
      >
        <GraficoEntradaSalida datos={movimientos} />
      </ChartCard>
    </div>
  );
}

export default PanelBodegas;